import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type AccentName,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  AUDIT,
  SHIPPED,
  HALFWIRED,
  PLANNED,
  NORTHSTAR,
  RULE,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

// Status pill — the load-bearing honesty device of this whole video.
const StatusPill: React.FC<{ label: string; accent: AccentName }> = ({
  label,
  accent,
}) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontFamily: FONTS.mono,
      fontSize: 16,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: COLORS[accent],
      border: `1px solid ${COLORS[accent]}`,
      borderRadius: 999,
      padding: "5px 14px",
    }}
  >
    <span
      style={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        background: COLORS[accent],
        boxShadow: `0 0 10px ${COLORS[accent]}`,
      }}
    />
    {label}
  </div>
);

// A scorecard-style row: claim + verdict pill, opacity reveal staggered.
const VerdictRow: React.FC<{
  label: string;
  sub: string;
  verdict: string;
  accent: AccentName;
  reveal: number;
}> = ({ label, sub, verdict, accent, reveal }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 30,
      width: 1180,
      background: COLORS.panel,
      border: `1px solid ${COLORS.line}`,
      borderLeft: `4px solid ${COLORS[accent]}`,
      borderRadius: 10,
      padding: "16px 24px",
      opacity: reveal,
      transform: `translateX(${(1 - reveal) * 18}px)`,
    }}
  >
    <div>
      <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 30, color: COLORS.text }}>
        {label}
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 17, color: COLORS.dim, marginTop: 4 }}>
        {sub}
      </div>
    </div>
    <StatusPill label={verdict} accent={accent} />
  </div>
);

const useStep = (frames: number[]) => {
  const frame = useCurrentFrame();
  return frames.map((f) =>
    interpolate(frame, [f, f + 18], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );
};

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const x = interpolate(frame, [42, 74], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="i audited my own ai product" color={COLORS.amber} />
          <Big>
            I marked 3 things{" "}
            <span style={{ color: COLORS.green }}>“SHIPPED”</span>
            <br />
            that did{" "}
            <span style={{ color: COLORS.red, opacity: 0.35 + x * 0.65 }}>
              NOTHING
            </span>{" "}
            at runtime
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 02 — AUDIT (why) */
export const Scene02Audit: React.FC = () => {
  const [r1, r2, r3] = useStep([18, 44, 70]);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.audit.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="my definition of “shipped” was lying" color={COLORS.red} />
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ opacity: r1 }}>
              <Panel accent="red" width={1000}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text }}>
                  UI shell <span style={{ color: COLORS.dim }}>+ deferred backend</span> ={" "}
                  <b style={{ color: COLORS.red }}>not shipped</b>
                </div>
              </Panel>
            </div>
            <div style={{ opacity: r2 }}>
              <Panel accent="red" width={1000}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text }}>
                  migration <span style={{ color: COLORS.dim }}>with no caller</span> ={" "}
                  <b style={{ color: COLORS.red }}>not shipped</b>
                </div>
              </Panel>
            </div>
            <div style={{ opacity: r3 }}>
              <Panel accent="cyan" width={1000}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text }}>
                  the test:{" "}
                  <span style={{ color: COLORS.cyan }}>design said X · code does Y · match?</span>
                </div>
              </Panel>
            </div>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={AUDIT} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 03 — SHIPPED (🟢) */
export const Scene03Shipped: React.FC = () => {
  const frame = useCurrentFrame();
  const [r1, r2, r3] = useStep([14, 40, 66]);
  const counter = interpolate(frame, [20, 60], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.shipped.duration} />
      <AbsoluteFill style={center}>
        <div style={{ marginBottom: 18 }}>
          <StatusPill label="shipped · live in prod" accent="green" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "center" }}>
          <VerdictRow
            label="Code-aware Hybrid RAG"
            sub="tree-sitter AST chunking · adaptive-chunker.ts"
            verdict="🟢 live"
            accent="green"
            reveal={r1}
          />
          <VerdictRow
            label="Knowledge graph · RLS · 9 mission workflows"
            sub="src/lib/graph · Supabase RLS · 521+ tests"
            verdict="🟢 live"
            accent="green"
            reveal={r2}
          />
          <VerdictRow
            label="Source-maturity guard"
            sub="never narrates a plan as shipped · source-tier.ts"
            verdict="🟢 live"
            accent="green"
            reveal={r3}
          />
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginTop: 30, opacity: counter }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 44, color: COLORS.dim }}>
            code retrieval 8%
          </span>
          <span style={{ fontFamily: FONTS.display, fontSize: 40, color: COLORS.dim }}>→</span>
          <MetricCounter value={72} from={8} startFrame={20} durationFrames={50} accent="green" suffix="%" fontSize={96} />
        </div>
      </AbsoluteFill>
      <Caption lines={SHIPPED} accent="green" />
    </AbsoluteFill>
  );
};

/* 04 — HALF-WIRED (🟡) */
export const Scene04HalfWired: React.FC = () => {
  const [r1, r2] = useStep([18, 48]);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.halfwired.duration} />
      <AbsoluteFill style={center}>
        <div style={{ marginBottom: 18 }}>
          <StatusPill label="half-wired · don't call it green" accent="amber" />
        </div>
        <div style={{ display: "flex", gap: 26, alignItems: "stretch" }}>
          <div style={{ opacity: r1 }}>
            <Panel accent="green" title="this part shipped" width={500}>
              <div style={{ fontFamily: FONTS.body, fontSize: 26, lineHeight: 1.5, color: COLORS.text }}>
                12 skill kinds got <b>real appliers</b>
                <div style={{ fontFamily: FONTS.mono, fontSize: 17, color: COLORS.dim, marginTop: 8 }}>
                  P3.A · 2026-05-28 · was 1 of 12
                </div>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: r2 }}>
            <Panel accent="amber" title="this part didn't" width={500}>
              <div style={{ fontFamily: FONTS.body, fontSize: 26, lineHeight: 1.5, color: COLORS.text }}>
                recency router + <b>auto-apply gate</b>
                <div style={{ fontFamily: FONTS.mono, fontSize: 17, color: COLORS.amber, marginTop: 8 }}>
                  🟡 in progress — labelled, not hidden
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={HALFWIRED} accent="amber" />
    </AbsoluteFill>
  );
};

/* 05 — PLANNED (🔵 roadmap) */
const ROADMAP: PipeNode[] = [
  { label: "G0", value: "EVAL GATE", sub: "🔵 queued", accent: "cyan" },
  { label: "G3 / G4", value: "GRAPH SCHEMA", sub: "🔵 designed", accent: "violet" },
  { label: "G6", value: "DURABLE WORKER", sub: "🔵 queued", accent: "amber" },
  { label: "G8", value: "SELF-HEAL", sub: "🔵 designed", accent: "magenta" },
];
export const Scene05Planned: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.planned.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <div style={{ marginBottom: 22 }}>
        <StatusPill label="roadmap · NOT YET SHIPPED" accent="violet" />
      </div>
      <div style={{ width: 1660 }}>
        <PipelineGraph nodes={ROADMAP} startFrame={8} stepFrames={24} nodeWidth={300} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim, marginTop: 34 }}>
        a schema in the Postgres I already pay for — <span style={{ color: COLORS.text }}>not a graph database</span>
      </div>
    </AbsoluteFill>
    <Caption lines={PLANNED} accent="violet" />
  </AbsoluteFill>
);

/* 06 — NORTH STAR */
export const Scene06NorthStar: React.FC = () => {
  const frame = useCurrentFrame();
  const fade = interpolate(frame, [60, 95], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.northstar.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the north star" color={COLORS.lime} />
          <Big size={84}>
            Not more features.
            <br />
            <span style={{ color: COLORS.lime }}>The brain.</span>
          </Big>
          <div style={{ display: "flex", gap: 24, marginTop: 40, opacity: fade }}>
            <Callout label="next dispatch · G0" accent="cyan" width={400}>
              an <b style={{ color: COLORS.text }}>eval gate</b> — prove every retrieval change in CI
            </Callout>
            <Callout label="next dispatch · G6" accent="amber" width={400}>
              a <b style={{ color: COLORS.text }}>durable worker</b> — missions survive a restart
            </Callout>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={NORTHSTAR} accent="lime" />
    </AbsoluteFill>
  );
};

/* 07 — THE RULE */
export const Scene07Rule: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.rule.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="definition of shipped" color={COLORS.cyan} />
        <Big size={70}>
          Done = <span style={{ color: COLORS.green }}>observable end-to-end</span>,
          <br />
          no further code changes.
        </Big>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 30,
            color: COLORS.dim,
            marginTop: 36,
            textAlign: "center",
            maxWidth: 1300,
          }}
        >
          Partial gets an <span style={{ color: COLORS.amber }}>honest label</span> — tracker, commit,
          and on camera.
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={RULE} accent="cyan" />
  </AbsoluteFill>
);

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          Audit your own product.
          <br />
          <span style={{ color: COLORS.cyan }}>Label what isn’t done.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · build in public · honest roadmap
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
